/** Liste complète des démarches : recherche, filtres, tri, pagination. */

import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { tasksApi, type TaskQuery } from "../api/endpoints";
import { TaskList } from "../components/TaskItem";
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextField,
} from "../components/ui";
import { useDebounced, useDocumentTitle, useResource } from "../hooks/useApi";
import { pluralize } from "../lib/format";

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "priority", label: "Priorité" },
  { value: "due", label: "Échéance" },
  { value: "title", label: "Titre" },
  { value: "status", label: "Statut" },
  { value: "created", label: "Date d'ajout" },
];

export function TasksPage() {
  useDocumentTitle("Toutes les démarches");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 350);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [obligations, setObligations] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [authority, setAuthority] = useState("");
  const [sort, setSort] = useState(params.get("tri") === "echeance" ? "due" : "priority");
  const [direction, setDirection] = useState(
    params.get("tri") === "echeance" ? "asc" : "desc",
  );
  const [offset, setOffset] = useState(0);

  const onlyMissing = params.get("only_missing_information") === "true";
  const onlyNeedsVerification = params.get("only_needs_verification") === "true";
  const onlyExpectedEvents = params.get("only_expected_events") === "true";
  const horizonDays = params.get("horizon_days");

  /** Bascule un filtre booléen porté par l'URL (partageable, et restauré au retour). */
  const toggleFlag = (name: string, active: boolean) => {
    const next = new URLSearchParams(params);
    if (active) {
      next.delete(name);
    } else {
      next.set(name, "true");
    }
    setParams(next);
    setOffset(0);
  };

  const options = useResource(() => tasksApi.options(), []);
  const authorities = useResource(() => tasksApi.authorities(), []);

  const query = useMemo<TaskQuery>(
    () => ({
      search: debouncedSearch.trim() || undefined,
      status: statuses.length ? (statuses as TaskQuery["status"]) : undefined,
      obligation: obligations.length ? obligations : undefined,
      category: categories.length ? categories : undefined,
      authority: authority || undefined,
      only_missing_information: onlyMissing || undefined,
      only_needs_verification: onlyNeedsVerification || undefined,
      only_expected_events: onlyExpectedEvents || undefined,
      horizon_days: horizonDays ? Number(horizonDays) : undefined,
      sort,
      direction,
      limit: PAGE_SIZE,
      offset,
    }),
    [
      debouncedSearch,
      statuses,
      obligations,
      categories,
      authority,
      onlyMissing,
      onlyNeedsVerification,
      onlyExpectedEvents,
      horizonDays,
      sort,
      direction,
      offset,
    ],
  );

  const tasks = useResource(() => tasksApi.list(query), [JSON.stringify(query)]);

  const toggle = (
    value: string,
    list: string[],
    setList: (next: string[]) => void,
  ) => {
    setOffset(0);
    setList(
      list.includes(value) ? list.filter((item) => item !== value) : [...list, value],
    );
  };

  const activeFilterCount =
    statuses.length +
    obligations.length +
    categories.length +
    (authority ? 1 : 0) +
    (onlyMissing ? 1 : 0) +
    (onlyNeedsVerification ? 1 : 0) +
    (onlyExpectedEvents ? 1 : 0) +
    (horizonDays ? 1 : 0);

  const clearFilters = () => {
    setStatuses([]);
    setObligations([]);
    setCategories([]);
    setAuthority("");
    setSearch("");
    setOffset(0);
    setParams(new URLSearchParams());
  };

  return (
    <>
      <PageHeader
        title="Toutes les démarches"
        subtitle={
          tasks.data
            ? `${pluralize(tasks.data.total, "démarche")} ${
                activeFilterCount > 0 ? "correspondant aux filtres" : "au total"
              }.`
            : "Chargement…"
        }
        actions={
          <>
            {activeFilterCount > 0 && (
              <Button variant="quiet" onClick={clearFilters}>
                Réinitialiser les filtres ({activeFilterCount})
              </Button>
            )}
            <Button variant="primary" onClick={() => navigate("/demarches/nouvelle")}>
              Ajouter une démarche
            </Button>
          </>
        }
      />

      <div className="filters">
        <div className="filters__wide">
          <TextField
            label="Rechercher"
            type="search"
            placeholder="Titre, organisme, notes…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
          />
        </div>
        <SelectField
          label="Organisme"
          options={[
            ...(authorities.data ?? []).map((name) => ({ value: name, label: name })),
          ]}
          placeholder="Tous les organismes"
          value={authority}
          onChange={(event) => {
            setAuthority(event.target.value);
            setOffset(0);
          }}
        />
        <div className="row" style={{ gap: "0.5rem", alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <SelectField
              label="Trier par"
              options={SORT_OPTIONS}
              value={sort}
              onChange={(event) => {
                setSort(event.target.value);
                setDirection(event.target.value === "due" ? "asc" : "desc");
                setOffset(0);
              }}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDirection(direction === "asc" ? "desc" : "asc")}
            aria-label={
              direction === "asc" ? "Trier en ordre décroissant" : "Trier en ordre croissant"
            }
            title="Inverser l'ordre de tri"
          >
            {direction === "asc" ? "croissant" : "décroissant"}
          </Button>
        </div>
      </div>

      {options.data && (
        <div className="stack stack--tight" style={{ marginBottom: "1rem" }}>
          <div>
            <span className="visually-hidden">Filtrer par statut</span>
            <div className="chips">
              {options.data.statuses.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  pressed={statuses.includes(option.value)}
                  onToggle={() => toggle(option.value, statuses, setStatuses)}
                />
              ))}
            </div>
          </div>
          <div>
            <span className="visually-hidden">Filtrer par caractère obligatoire</span>
            <div className="chips">
              {options.data.obligations.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  pressed={obligations.includes(option.value)}
                  onToggle={() => toggle(option.value, obligations, setObligations)}
                />
              ))}
            </div>
          </div>
          <div>
            <span className="visually-hidden">Filtrer par catégorie</span>
            <div className="chips">
              {options.data.categories.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  pressed={categories.includes(option.value)}
                  onToggle={() => toggle(option.value, categories, setCategories)}
                />
              ))}
            </div>
          </div>
          <div>
            {/* Ces trois filtres n'étaient atteignables que par un lien du
                tableau de bord. Les exposer ici les rend trouvables. */}
            <span className="visually-hidden">Filtres particuliers</span>
            <div className="chips">
              <Chip
                label="À attendre et confirmer"
                pressed={onlyExpectedEvents}
                onToggle={() =>
                  toggleFlag("only_expected_events", onlyExpectedEvents)
                }
              />
              <Chip
                label="Information manquante"
                pressed={onlyMissing}
                onToggle={() =>
                  toggleFlag("only_missing_information", onlyMissing)
                }
              />
              <Chip
                label="Donnée à vérifier"
                pressed={onlyNeedsVerification}
                onToggle={() =>
                  toggleFlag("only_needs_verification", onlyNeedsVerification)
                }
              />
            </div>
          </div>
        </div>
      )}

      <ErrorAlert error={tasks.error} title="Liste indisponible" />

      <Card flush>
        {tasks.loading ? (
          <LoadingBlock />
        ) : !tasks.data || tasks.data.items.length === 0 ? (
          <EmptyState
            title="Aucune démarche ne correspond"
            action={
              activeFilterCount > 0 ? (
                <Button variant="ghost" onClick={clearFilters}>
                  Réinitialiser les filtres
                </Button>
              ) : (
                <Button variant="primary" onClick={() => navigate("/demarches/nouvelle")}>
                  Ajouter une démarche
                </Button>
              )
            }
          >
            {activeFilterCount > 0
              ? "Essayez d'élargir votre recherche."
              : "Votre checklist se remplira dès que votre profil entreprise sera complété."}
          </EmptyState>
        ) : (
          <TaskList tasks={tasks.data.items} showWhy />
        )}
      </Card>

      {tasks.data && tasks.data.total > PAGE_SIZE && (
        <nav className="row row--between mt-4" aria-label="Pagination">
          <Button
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Page précédente
          </Button>
          <span className="inline-note">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, tasks.data.total)} sur{" "}
            {tasks.data.total}
          </span>
          <Button
            variant="ghost"
            disabled={!tasks.data.has_more}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Page suivante
          </Button>
        </nav>
      )}
    </>
  );
}
